import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
} from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { downloadTemplate } from 'services/MassiveService';
import { downloadOrNotify } from 'components/massive/download';
import { APP_ROLE_STORE } from 'constants/roles';
import { authProfile } from 'redux/auth/selectors';
import { storeItemsSelector } from 'redux/store/selectors';
import useStore from 'redux/store';
import {
  Massive,
  MassiveListFilters,
  MassiveListItem,
  MassiveStatus,
} from 'services/MassiveService';
import { useMassive, useMassiveList } from 'queries/useMassiveQuery';
import MassiveStatusTag from 'components/massive/statusTag';
import MassiveOriginBadge from 'components/massive/originBadge';
import MassiveLabelsButton from 'components/massive/labelsButton';
import MassiveUploadModal from 'components/massive/uploadModal';
import MassivePackagesModal from 'components/massive/packagesModal';
import MassiveAuditModal from 'components/massive/auditModal';
import MassiveDeleteModal from 'components/massive/deleteModal';
import MassiveErrorsModal from 'components/massive/errorsModal';
import MassiveServiceError from 'components/massive/serviceError';
import { MASSIVE_SCOPES } from 'components/massive/options';
import { STATUS_OPTIONS } from 'components/massive/status';

const PAGE_SIZE = 20;

interface Store {
  id: number;
  name: string;
}

const MassiveSegaList = () => {
  useStore();

  const history = useHistory();

  const profile = useSelector(authProfile);
  const stores = useSelector(storeItemsSelector) as Store[];

  const isStoreUser = profile?.AppRole === APP_ROLE_STORE;
  const scope = isStoreUser ? MASSIVE_SCOPES.STORE : MASSIVE_SCOPES.MARKETPLACE;

  const [filters, setFilters] = useState<MassiveListFilters>({
    page: 0,
    size: PAGE_SIZE,
  });
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<MassiveStatus | undefined>();
  const [storeId, setStoreId] = useState<number | undefined>();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [packagesOf, setPackagesOf] = useState<string | undefined>();
  const [auditOf, setAuditOf] = useState<string | undefined>();
  const [deleteOf, setDeleteOf] = useState<string | undefined>();
  const [errorsOf, setErrorsOf] = useState<string | undefined>();

  const { data, isFetching, isError, error, refetch } = useMassiveList(filters);
  const detail = useMassive(auditOf ?? deleteOf);

  const storeOptions = useMemo(
    () =>
      (stores ?? []).map((store) => ({ value: store.id, label: store.name })),
    [stores],
  );

  const statusName = (id: number) => String(id);

  const search = () =>
    setFilters({
      from: range?.[0]?.startOf('day').toISOString(),
      to: range?.[1]?.endOf('day').toISOString(),
      code: code || undefined,
      status,
      storeId,
      page: 0,
      size: PAGE_SIZE,
    });

  const clear = () => {
    setRange(null);
    setCode('');
    setStatus(undefined);
    setStoreId(undefined);
    setFilters({ page: 0, size: PAGE_SIZE });
  };

  const columns = [
    {
      title: 'Código',
      key: 'massive_id',
      width: 260,
      render: (_: unknown, item: MassiveListItem) => (
        <Space direction="vertical" size={2}>
          <span>{item.massive_id}</span>
          <MassiveOriginBadge creator={item.creator} scope={scope} />
        </Space>
      ),
    },
    {
      title: 'Tienda',
      key: 'origin',
      render: (_: unknown, item: MassiveListItem) =>
        (item.stores ?? []).map((store) => store.name).join(', ') || '—',
    },
    {
      title: 'Fecha de creación',
      dataIndex: 'created_at',
      width: 170,
      render: (value: string) => dayjs(value).format('DD/MM/YY, HH:mm'),
    },
    {
      title: 'Estado',
      key: 'status',
      width: 220,
      render: (_: unknown, item: MassiveListItem) => (
        <MassiveStatusTag
          status={item.status}
          missingLabels={item.missing_labels}
          reason={item.last_error}
        />
      ),
    },
    {
      title: 'Creados',
      key: 'created',
      width: 110,
      align: 'center' as const,
      render: (_: unknown, item: MassiveListItem) =>
        `${item.created_count} / ${item.total}`,
    },
    {
      title: 'Con errores',
      key: 'failed',
      width: 120,
      align: 'center' as const,
      render: (_: unknown, item: MassiveListItem) => {
        const total = item.failed_count + item.rejected_count;
        if (total === 0) return 0;
        return (
          <Button type="link" onClick={() => setErrorsOf(item.massive_id)}>
            {total}
          </Button>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 210,
      align: 'center' as const,
      render: (_: unknown, item: MassiveListItem) => (
        <Space>
          <MassiveLabelsButton
            massiveId={item.massive_id}
            createdCount={item.created_count}
            disabled={item.status === 'cancelled'}
          />
          <Tooltip title="Paquetes">
            <Button
              icon={<UnorderedListOutlined />}
              onClick={() => setPackagesOf(item.massive_id)}
            />
          </Tooltip>
          <Tooltip title="Auditoría">
            <Button
              icon={<InfoCircleOutlined />}
              onClick={() => setAuditOf(item.massive_id)}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={item.status === 'cancelled'}
              onClick={() => setDeleteOf(item.massive_id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Alta masiva"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} />
          <Button
            icon={<DownloadOutlined />}
            onClick={() =>
              downloadOrNotify(downloadTemplate, 'alta-masiva.xlsx')
            }
          >
            Descargar template
          </Button>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={() => setUploadOpen(true)}
          >
            Subir
          </Button>
        </Space>
      }
    >
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col>
          <DatePicker.RangePicker
            value={range}
            onChange={(value) => setRange(value as [Dayjs, Dayjs] | null)}
            format="DD/MM/YYYY"
          />
        </Col>
        <Col>
          <Input
            placeholder="Código de masiva"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            allowClear
            style={{ width: 220 }}
          />
        </Col>
        {isStoreUser ? null : (
          <Col>
            <Select
              placeholder="Tienda"
              allowClear
              showSearch
              optionFilterProp="label"
              options={storeOptions}
              value={storeId}
              onChange={setStoreId}
              style={{ width: 200 }}
            />
          </Col>
        )}
        <Col>
          <Select
            placeholder="Estado"
            allowClear
            options={STATUS_OPTIONS}
            value={status}
            onChange={setStatus}
            style={{ width: 180 }}
          />
        </Col>
        <Col>
          <Space>
            <Button type="primary" onClick={search}>
              Buscar
            </Button>
            <Button onClick={clear}>Limpiar</Button>
          </Space>
        </Col>
      </Row>

      {isError ? (
        <MassiveServiceError
          error={error}
          title="No se pudieron cargar las masivas"
          onRetry={() => refetch()}
        />
      ) : (
        <Table
          rowKey="massive_id"
          loading={isFetching}
          columns={columns}
          dataSource={data?.content ?? []}
          pagination={{
            current: (filters.page ?? 0) + 1,
            pageSize: filters.size ?? PAGE_SIZE,
            total: data?.total ?? 0,
            showSizeChanger: false,
            onChange: (page) =>
              setFilters((prev) => ({ ...prev, page: page - 1 })),
          }}
        />
      )}

      <MassiveUploadModal
        open={uploadOpen}
        scope={scope}
        marketplaces={[]}
        stores={storeOptions}
        onClose={() => setUploadOpen(false)}
        onCreated={(massiveId) => setPackagesOf(massiveId)}
      />

      <MassivePackagesModal
        open={!!packagesOf}
        massiveId={packagesOf}
        onClose={() => setPackagesOf(undefined)}
        onViewPackage={(daliInternalId) =>
          history.push(`/packages/view/${daliInternalId}`)
        }
      />

      <MassiveErrorsModal
        open={!!errorsOf}
        massiveId={errorsOf}
        onClose={() => setErrorsOf(undefined)}
      />

      <MassiveAuditModal
        open={!!auditOf}
        massive={detail.data as Massive | undefined}
        error={detail.error}
        onClose={() => setAuditOf(undefined)}
      />

      <MassiveDeleteModal
        open={!!deleteOf}
        massive={detail.data as Massive | undefined}
        error={detail.error}
        statusName={statusName}
        onClose={() => setDeleteOf(undefined)}
      />
    </Card>
  );
};

export default MassiveSegaList;
